// source: template-project@7007a338a apps/backoffice/src/components/ui/agent-orb.tsx
import { cn } from '@/lib/utils';

/**
 * AgentOrb — the DS v2 identity mark for an AI agent.
 *
 * A perfect circle filled with three layered `radial-gradient`s over a
 * `linear-gradient` base, plus an inset rim light + bottom shading so it reads
 * as a small sphere rather than a flat dot. It carries NO text — identity comes
 * from the name/role rendered next to it. Humans keep the initials avatar; the
 * orb is reserved for agents, and that distinction is the point.
 *
 * The hue variant is derived deterministically from the agent id, so a given
 * agent always gets the same orb across every screen. Never hand-pick a variant
 * per screen — always go through `agentOrb(id)` / `<AgentOrb id />`.
 */

type OrbVariant = {
  /** Full CSS `background` stack: 3 radial highlights over a linear base. */
  background: string;
};

const ORB_VARIANTS: readonly OrbVariant[] = [
  // 0 · violet → pink
  {
    background: [
      'radial-gradient(58% 55% at 30% 26%, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0) 58%)',
      'radial-gradient(85% 75% at 78% 82%, #b0308f 0%, rgba(176,48,143,0) 70%)',
      'radial-gradient(55% 50% at 72% 30%, #a970ff 0%, rgba(169,112,255,0) 62%)',
      'linear-gradient(150deg, #7c5cff 0%, #c452c9 100%)',
    ].join(', '),
  },
  // 1 · pink → violet
  {
    background: [
      'radial-gradient(58% 55% at 32% 24%, rgba(255,255,255,0.52) 0%, rgba(255,255,255,0) 58%)',
      'radial-gradient(85% 75% at 80% 84%, #6d28d9 0%, rgba(109,40,217,0) 70%)',
      'radial-gradient(55% 50% at 70% 32%, #ff77c8 0%, rgba(255,119,200,0) 62%)',
      'linear-gradient(150deg, #ff5fa2 0%, #8b5cf6 100%)',
    ].join(', '),
  },
  // 2 · blue → cyan
  {
    background: [
      'radial-gradient(58% 55% at 30% 26%, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0) 58%)',
      'radial-gradient(85% 75% at 80% 82%, #0b5cc4 0%, rgba(11,92,196,0) 70%)',
      'radial-gradient(55% 50% at 72% 30%, #57e0ff 0%, rgba(87,224,255,0) 62%)',
      'linear-gradient(150deg, #3b7dff 0%, #22bdf0 100%)',
    ].join(', '),
  },
  // 3 · coral → violet
  {
    background: [
      'radial-gradient(58% 55% at 32% 24%, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0) 58%)',
      'radial-gradient(85% 75% at 80% 84%, #7c3aed 0%, rgba(124,58,237,0) 70%)',
      'radial-gradient(55% 50% at 70% 30%, #ff9d76 0%, rgba(255,157,118,0) 62%)',
      'linear-gradient(150deg, #f2694d 0%, #9b5cf6 100%)',
    ].join(', '),
  },
  // 4 · emerald → teal
  {
    background: [
      'radial-gradient(58% 55% at 30% 26%, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0) 58%)',
      'radial-gradient(85% 75% at 80% 82%, #0f766e 0%, rgba(15,118,110,0) 70%)',
      'radial-gradient(55% 50% at 72% 30%, #6ee7b7 0%, rgba(110,231,183,0) 62%)',
      'linear-gradient(150deg, #10b981 0%, #14b8a6 100%)',
    ].join(', '),
  },
  // 5 · amber → coral
  {
    background: [
      'radial-gradient(58% 55% at 32% 24%, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0) 58%)',
      'radial-gradient(85% 75% at 80% 84%, #c2410c 0%, rgba(194,65,12,0) 70%)',
      'radial-gradient(55% 50% at 70% 30%, #fde68a 0%, rgba(253,230,138,0) 62%)',
      'linear-gradient(150deg, #f59e0b 0%, #f97362 100%)',
    ].join(', '),
  },
  // 6 · indigo → sky
  {
    background: [
      'radial-gradient(58% 55% at 30% 26%, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0) 58%)',
      'radial-gradient(85% 75% at 80% 82%, #3730a3 0%, rgba(55,48,163,0) 70%)',
      'radial-gradient(55% 50% at 72% 30%, #a5b4fc 0%, rgba(165,180,252,0) 62%)',
      'linear-gradient(150deg, #4f46e5 0%, #38bdf8 100%)',
    ].join(', '),
  },
  // 7 · magenta → orange
  {
    background: [
      'radial-gradient(58% 55% at 32% 24%, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0) 58%)',
      'radial-gradient(85% 75% at 80% 84%, #9d174d 0%, rgba(157,23,77,0) 70%)',
      'radial-gradient(55% 50% at 70% 30%, #fdba74 0%, rgba(253,186,116,0) 62%)',
      'linear-gradient(150deg, #db2777 0%, #fb923c 100%)',
    ].join(', '),
  },
];

/**
 * The roster's seats, each with its own variant. A hash over the variants
 * cannot promise two agents different colours — with six seats and four hues
 * it collided twice (planner/creative-strategist, researcher/funnel-cloner) —
 * so the known seats are assigned outright. Seats that already had a hue to
 * themselves keep it; only the two colliders move to new hues. A seat not
 * listed here still gets a stable hash-derived variant.
 */
export const AGENT_ORB_ASSIGNMENTS: Readonly<Record<string, number>> = {
  planner: 2,
  researcher: 0,
  'ad-chief': 3,
  'funnel-vibe-coder': 1,
  'creative-strategist': 4,
  'funnel-cloner': 5,
};

/** Rim light + bottom shading — the same in both themes (it's a lit sphere). */
const ORB_SHADOW = 'inset 0 0 0 1px rgba(255,255,255,0.14), inset 0 -6px 12px rgba(0,0,0,0.30)';

/** Deterministic variant index for an agent id (stable across renders/screens). */
export function agentOrbVariant(id: string): number {
  const assigned = AGENT_ORB_ASSIGNMENTS[id];
  if (assigned !== undefined) return assigned;
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return hash % ORB_VARIANTS.length;
}

/** How many hues exist — the roster can grow this far before two seats must share. */
export const AGENT_ORB_VARIANT_COUNT = ORB_VARIANTS.length;

/** The inline style for an agent orb — use when you can't render <AgentOrb />. */
export function agentOrb(id: string): { background: string; boxShadow: string } {
  return { background: ORB_VARIANTS[agentOrbVariant(id)]!.background, boxShadow: ORB_SHADOW };
}

/** Recommended orb sizes (px): rosters/lists, inline chat, dense rows. */
export type AgentOrbSize = 34 | 26 | 22;

export function AgentOrb({
  id,
  size = 34,
  className,
  title,
}: {
  id: string;
  size?: number;
  className?: string;
  title?: string;
}) {
  return (
    <span
      className={cn('inline-block shrink-0 rounded-full', className)}
      style={{ width: size, height: size, ...agentOrb(id) }}
      title={title}
      aria-hidden
    />
  );
}
